import React, { useState, useRef, useEffect, useCallback } from 'react';

type EditTrigger = 'click' | 'hover' | 'focus';

type InlineEditProps<T> = {
  value: T;
  onChange: (value: T) => void;
  renderDisplay: (value: T) => React.ReactNode;
  renderEditor: (
    value: T,
    onChange: (value: T) => void,
    onBlur: (currentValue?: T) => void,
    onFocus: () => void
  ) => React.ReactNode;
  editOn?: EditTrigger;
  disabled?: boolean;
  className?: string;
  onBlur?: (value: T) => void;
};

export function InlineEdit<T>({
  value,
  onChange,
  renderDisplay,
  renderEditor,
  editOn = 'hover',
  disabled = false,
  className,
  onBlur,
}: InlineEditProps<T>) {
  const [isEditing, setIsEditing] = useState(false);
  const [isHovering, setIsHovering] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  // Track the current value to use when blur/click-outside happens
  const currentValueRef = useRef<T>(value);

  // Sync ref when prop value changes externally
  React.useEffect(() => {
    currentValueRef.current = value;
  }, [value]);

  const handleStartEdit = () => {
    if (!disabled) {
      setIsEditing(true);
    }
  };

  const handleEndEdit = useCallback(
    (currentValue?: T) => {
      if (onBlur) {
        onBlur(currentValue !== undefined ? currentValue : currentValueRef.current);
      }
      setIsEditing(false);
      setIsHovering(false);
    },
    [onBlur]
  );

  const handleChange = (newValue: T) => {
    // Update the ref whenever the value changes
    currentValueRef.current = newValue;
    onChange(newValue);
  };

  // Handle click outside to end editing
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!isEditing) return;

      const target = event.target as Node;

      // Check if click is inside the container
      if (containerRef.current && containerRef.current.contains(target)) {
        return;
      }

      // Check if click is inside a ReactDatePicker calendar (rendered in portal)
      const datePickerElement = (target as Element).closest?.('.react-datepicker');
      if (datePickerElement) {
        return;
      }

      // Check if click is inside a react-select menu (rendered in portal)
      const selectMenuElement = (target as Element).closest?.('.react-select__menu');
      if (selectMenuElement) {
        return;
      }

      // Click is truly outside, end editing
      handleEndEdit();
    };

    if (isEditing) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [isEditing, handleEndEdit]);

  const shouldShowEditor = isEditing || (editOn === 'hover' && isHovering);

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      ref={containerRef}
      className={className}
      onMouseEnter={() => {
        if (editOn === 'hover' && !disabled) {
          setIsHovering(true);
        }
      }}
      onMouseLeave={() => {
        if (editOn === 'hover') {
          setIsHovering(false);
        }
      }}
      onClick={() => {
        if (editOn === 'click' && !disabled) {
          handleStartEdit();
        }
      }}
      onFocus={() => {
        if (editOn === 'focus' && !disabled) {
          handleStartEdit();
        }
      }}
    >
      {shouldShowEditor ? (
        <div>{renderEditor(value, handleChange, handleEndEdit, handleStartEdit)}</div>
      ) : (
        <button
          type="button"
          onClick={() => {
            if (!disabled) {
              handleStartEdit();
            }
          }}
          onKeyDown={(e) => {
            if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
              e.preventDefault();
              handleStartEdit();
            }
          }}
          disabled={disabled}
          className="tw:w-full tw:text-left tw:px-[calc(theme(spacing[2])-1px)] tw:py-[calc(theme(spacing[0.5])-1px)] tw:sm:px-[calc(theme(spacing[1.5])-1px)] tw:sm:py-[1px] tw:text-xs/4 tw:text-zinc-950 tw:sm:text-[10px]/4 tw:dark:text-white tw:cursor-text tw:focus:outline-none tw:focus:ring-2 tw:focus:ring-brand-green-0 tw:rounded-lg tw:disabled:opacity-50 tw:disabled:cursor-not-allowed"
        >
          {renderDisplay(value)}
        </button>
      )}
    </div>
  );
}
